import { Icon } from '../../components/Icon.tsx';
import type { Product } from './products.ts';
import { SpecimenPlate } from './SpecimenPlate.tsx';
import { SCREENS } from './screens/index.tsx';

/**
 * One product presented in full on the Applications page: tonal chips and the
 * host, a copy block beside its browser plate, then a grid of Material 3 filled
 * feature cards. Sides and the tonal ground alternate per entry, matching the
 * landing's ProductSection rhythm.
 */
export function AppFeatureSection({
  product,
  index,
}: {
  product: Product;
  index: number;
}): JSX.Element {
  const flip = index % 2 === 1;
  const Screen = SCREENS[product.key];
  const host = product.url.replace(/^https?:\/\//, '');
  // On the tonal band the cards lift to the next surface tier so they still read.
  const cardTone = flip ? 'var(--surface-container-low)' : 'var(--surface-container)';

  return (
    <section
      id={product.key}
      className={`scroll-mt-24 ${flip ? 'bg-[hsl(var(--surface-container))]' : ''}`}
    >
      <div className="folio-wide py-16 sm:py-24">
        {/* entry header — tonal chips and the host, no rule */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <span className="chip-dv">
            <Icon name={product.icon} size={16} />
            {product.category}
          </span>
          {product.inDevelopment && (
            <span
              className="chip-dv"
              style={{
                background: 'hsl(var(--warning-container))',
                color: 'hsl(var(--on-warning-container))',
              }}
            >
              <span className="head-dv leading-none">ޑިވެލޮޕްކުރަމުން ދަނީ</span>
            </span>
          )}
          <span className="ms-auto hidden text-sm text-muted-foreground sm:inline" dir="ltr">
            {host}
          </span>
        </div>

        {/* intro + specimen */}
        <div className="mt-9 grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <div className={`min-w-0 ${flip ? 'lg:order-2' : ''}`}>
            <h2 className="head-dv text-3xl leading-tight sm:text-[2.6rem] sm:leading-[1.2]">
              {product.name}
            </h2>
            <p className="head-dv mt-4 text-xl text-[hsl(var(--primary))]">{product.tagline}</p>
            <p className="mt-4 max-w-md leading-loose text-muted-foreground">{product.blurb}</p>
            <a
              href={product.url}
              target="_blank"
              rel="noreferrer"
              className="btn-secondary mt-8"
            >
              {product.name} ހުޅުވާ <Icon name="open_in_new" size={18} />
            </a>
          </div>

          <div className={`min-w-0 ${flip ? 'lg:order-1' : ''}`}>
            <SpecimenPlate host={host}>{Screen ? <Screen /> : null}</SpecimenPlate>
          </div>
        </div>

        {/* detailed capabilities — M3 filled cards, each led by a tonal icon
            badge. Tone and space do the separating; there are no rules. */}
        <ul className="mt-12 grid grid-cols-1 gap-3 sm:mt-16 sm:grid-cols-2 lg:grid-cols-3">
          {product.features.map((f) => (
            <li
              key={f.title}
              className="flex gap-4 rounded-[var(--radius)] p-5"
              style={{ background: `hsl(${cardTone})` }}
            >
              <span
                className="inline-flex size-10 shrink-0 items-center justify-center rounded-full"
                style={{
                  background: 'hsl(var(--primary-container))',
                  color: 'hsl(var(--on-primary-container))',
                }}
                aria-hidden
              >
                <Icon name={f.icon} size={20} />
              </span>
              <div className="min-w-0">
                <h3 className="head-dv text-lg leading-snug">{f.title}</h3>
                <p className="mt-2 text-sm leading-loose text-muted-foreground">{f.desc}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
